import { useState, useCallback } from "react";
import { ChevronDown, ChevronUp, Layers } from "lucide-react";
import type { ITask } from "../types";
import { SwipeableItem, type SwipeAction } from "./SortableTaskList";

type SwipeStartProp = SwipeAction | ((task: ITask) => SwipeAction | null);

interface TaskStackProps {
  /** Tasks ordered top→bottom (most-prerequisite first, root last). */
  tasks: ITask[];
  /** UUIDs eligible for "Move to Today" (no incomplete prereqs). */
  eligibleUuids: Set<string>;
  showMoveToToday?: boolean;
  showMoveToBacklog?: boolean;
  showComplete?: boolean;
  showStatus?: boolean;
  swipeStart?: SwipeStartProp;
  swipeEnd?: SwipeAction;
  swipeEndSecondary?: SwipeAction | ((task: ITask) => SwipeAction | null);
}

function gateSwipeStart(
  swipeStart: SwipeStartProp | undefined,
  eligibleUuids: Set<string>
): SwipeStartProp | undefined {
  if (!swipeStart) return undefined;
  return (task: ITask) => {
    const resolved =
      typeof swipeStart === "function" ? swipeStart(task) : swipeStart;
    if (!resolved) return null;
    const isMoveToToday = /today/i.test(resolved.label);
    if (isMoveToToday && !eligibleUuids.has(task.uuid)) return null;
    return resolved;
  };
}

export default function TaskStack({
  tasks,
  eligibleUuids,
  showMoveToToday,
  showMoveToBacklog,
  showComplete,
  showStatus,
  swipeStart,
  swipeEnd,
  swipeEndSecondary,
}: TaskStackProps) {
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  if (tasks.length === 0) return null;

  const topTask = tasks[0];
  const hiddenCount = tasks.length - 1;
  const gatedSwipeStart = gateSwipeStart(swipeStart, eligibleUuids);

  if (!expanded) {
    // Always show a consistent 2-peek silhouette when there are any hidden tasks.
    const peekReserve = hiddenCount > 0 ? 18 : 0;

    return (
      <div
        className="relative"
        data-testid="task-stack"
        data-expanded={false}
        style={{ paddingBottom: peekReserve }}
      >
        {/* Peek sliver #2 — furthest back, narrower, most faded */}
        {hiddenCount > 0 && (
          <div
            aria-hidden
            className="absolute left-4 right-4 h-2 bg-surface border border-border rounded-b-lg opacity-40 pointer-events-none"
            style={{ bottom: 2 }}
          />
        )}
        {/* Peek sliver #1 — closer to top card */}
        {hiddenCount > 0 && (
          <div
            aria-hidden
            className="absolute left-2 right-2 h-2 bg-surface border border-border rounded-b-lg opacity-75 pointer-events-none"
            style={{ bottom: 7 }}
          />
        )}

        {/* Top card */}
        <div className="relative">
          <SwipeableItem
            task={topTask}
            showMoveToToday={showMoveToToday}
            showMoveToBacklog={showMoveToBacklog}
            showComplete={showComplete}
            showStatus={showStatus}
            swipeStart={gatedSwipeStart}
            swipeEnd={swipeEnd}
            swipeEndSecondary={swipeEndSecondary}
          />
        </div>

        {/* Floating "+N more" pill — anchored to bottom-right of the stack */}
        <button
          type="button"
          onClick={toggle}
          aria-label={`Expand dependency chain of ${tasks.length} tasks`}
          className="absolute right-3 z-20 inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-primary text-primary-foreground text-[10px] font-semibold shadow-sm hover:bg-primary/90 transition-colors"
          style={{ bottom: -2 }}
        >
          <Layers className="w-3 h-3" />
          {hiddenCount} more
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl border border-primary/20 bg-primary/[0.04] px-2.5 py-2.5"
      data-testid="task-stack"
      data-expanded={true}
    >
      <div className="flex items-center justify-between mb-2 px-0.5">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary">
          <Layers className="w-3.5 h-3.5" />
          Dependency chain · {tasks.length} tasks
        </div>
        <button
          type="button"
          onClick={toggle}
          aria-label="Collapse stack"
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold hover:bg-primary/20 transition-colors"
        >
          <ChevronUp className="w-3 h-3" />
          Collapse
        </button>
      </div>

      <div className="relative pl-5 space-y-2">
        {/* Vertical rail connecting chain nodes */}
        <div
          aria-hidden
          className="absolute left-[7px] top-4 bottom-4 w-px bg-primary/25"
        />
        {tasks.map((task, idx) => (
          <div key={task.uuid} className="relative">
            {/* Rail node (filled on the first/root task) */}
            <div
              aria-hidden
              className={`absolute -left-[15px] top-[18px] w-2.5 h-2.5 rounded-full border-[1.5px] ${
                idx === 0
                  ? "bg-primary border-primary ring-4 ring-primary/15"
                  : "bg-surface border-primary/50"
              }`}
            />
            <SwipeableItem
              task={task}
              showMoveToToday={showMoveToToday}
              showMoveToBacklog={showMoveToBacklog}
              showComplete={showComplete}
              showStatus={showStatus}
              swipeStart={gatedSwipeStart}
              swipeEnd={swipeEnd}
              swipeEndSecondary={swipeEndSecondary}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
